import { useState } from "react";
import { ApiError, inviteAuditor } from "../api/client";
import { SelectField } from "../components/SelectField";
import { TextField } from "../components/TextField";
import { idempotencyKey } from "../api/idempotency";

const ENGAGEMENTS = ["Employee", "Contracted external auditor"] as const;

/**
 * Screen 21 — the invitation modal.
 *
 * A name, an address, an engagement and a *proposal*. It carries no competence, and it
 * must not: the auditor asserts their own and the certification body verifies it
 * afterwards, which is the whole shape of Cl. 7. A body that could record competence on
 * someone's behalf would then be verifying its own entry.
 *
 * The standards offered are limited to the certification body's accredited scope, as the
 * contract requires — proposing an auditor for a standard the body may not certify against
 * proposes nothing. The server refuses one anyway; this only avoids offering it.
 */
export function InviteAuditor({
  standards,
  onClose,
  onInvited,
}: {
  standards: readonly string[];
  onClose: () => void;
  onInvited: () => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [engagement, setEngagement] = useState<string>(ENGAGEMENTS[0]);
  const [proposed, setProposed] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** One key per invitation, minted once — a retry must be the same invitation. */
  const [key] = useState(() => `invitation-${idempotencyKey()}`);

  const toggle = (standard: string) => {
    setProposed((current) =>
      current.includes(standard)
        ? current.filter((entry) => entry !== standard)
        : [...current, standard],
    );
  };

  const send = async () => {
    if (!name.trim() || !email.trim()) {
      setError("Name and email are required.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await inviteAuditor(
        {
          name: name.trim(),
          email: email.trim(),
          engagement,
          proposed_standards: proposed,
        },
        key,
      );
      onInvited();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "The invitation was not sent.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="scrim"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-label="Invite an auditor">
        <h2>Invite an auditor</h2>
        <p className="sub" style={{ marginBottom: "14px" }}>
          The auditor completes their own competence record and signs the declarations. You
          then verify it. They cannot be allocated to an audit until you do.
        </p>

        <div className="grid2">
          <TextField
            id="invite-name"
            label="Full name"
            value={name}
            onChange={setName}
            required
          />
          <TextField
            id="invite-email"
            label="Email"
            kind="email"
            placeholder="name@example"
            value={email}
            onChange={setEmail}
            required
          />
        </div>

        <SelectField
          id="invite-engagement"
          label="Engagement"
          value={engagement}
          onChange={setEngagement}
          options={ENGAGEMENTS.map((value) => ({ value, label: value }))}
        />

        <div className="field">
          <span className="f">Standards you propose them for</span>
          <div className="chips">
            {standards.map((standard) => (
              <button
                key={standard}
                type="button"
                className="chip"
                aria-pressed={proposed.includes(standard)}
                onClick={() => toggle(standard)}
              >
                {standard}
              </button>
            ))}
          </div>
          <div className="sub" style={{ fontSize: "11.5px", marginTop: "6px" }}>
            Limited to your accredited standards.
          </div>
        </div>

        {error ? (
          <p className="err" role="alert">
            {error}
          </p>
        ) : null}

        <div className="mfoot">
          <button type="button" className="btn sec" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn" disabled={busy} onClick={() => void send()}>
            {busy ? "Sending…" : "Send invitation"}
          </button>
        </div>
      </div>
    </div>
  );
}
